import FormNav, {
    FormAgentParts,
    FormParts,
} from "@/app/shared/orders/create-edit/form-nav";
import FormOrderInfo from "@/app/shared/orders/create-edit/form-order-info";
import { defaultValues } from "@/app/shared/orders/create-edit/form-utils";
import FormFooter from "@/components/form-footer";
import { LAYOUT_OPTIONS } from "@/config/enums";
import { useLayout } from "@/layouts/use-layout";
import { Category, Order } from "@/types";
import cn from "@/utils/class-names";
import { OrderFormSchema } from "@/validators/create-orderInfo.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Element } from "react-scroll";
import {
    createOrder,
    fetchCategories,
    updateOrder,
} from "@/server/api-services";
// import { useRouter } from "next/router";
import routes from "@/routers/routes";
import { useNavigate } from "react-router-dom";
import FormOrderAgentInfo from "./form-order-info-agent";

const MAP_STEP_TO_COMPONENT = {
    [FormParts.OrderInfo]: FormOrderInfo,
};

const MAP_AGENT_STEP_TO_COMPONENT = {
    [FormAgentParts.OrderAgentInfo]: FormOrderAgentInfo,
};

interface IndexProps {
    id?: string;
    className?: string;
    order?: Order;
    isEdit?: boolean;
    isAgent: boolean;
}

export default function CreateEditOrder({
    id,
    order,
    className,
    isEdit,
    isAgent,
}: IndexProps) {
    const navigate = useNavigate();
    const { layout } = useLayout();
    const [isLoading, setLoading] = useState(false);
    // const router = useRouter();

    const methods = useForm<Order>({
        defaultValues: defaultValues(order),
        resolver: zodResolver(OrderFormSchema),
    });

    useEffect(() => {
        if (isEdit && order) {
            methods.reset(
                defaultValues({
                    ...order,
                    deliveryDate: "",
                    createdDate: "",
                }),
            );
        }
    }, [isEdit, order]);

    const onSubmit: SubmitHandler<Order> = async (data) => {
        setLoading(true);
        try {
            if (isEdit) {
                await updateOrder(data.id, {
                    ...data,
                    product: {
                        ...data.product,
                        categoryId: (data.product.categoryId as any).value,
                    },
                });
            } else {
                await createOrder({
                    ...data,
                    product: {
                        ...data.product,
                        categoryId: (data.product.categoryId as any).value,
                    },
                });
            }
            navigate(routes.orders.list);
        } catch (error) {
            toast.error("Failed to create order");
            console.error("Failed to create order", error);
        } finally {
            setLoading(false);
        }
    };

    console.log("errors", methods.formState?.errors);

    const [categories, setCategories] = useState<Category[]>([]);

    useEffect(() => {
        getCategories();
    }, []);

    const getCategories = async () => {
        const response = await fetchCategories();
        if (response.statusCode !== 200) {
            toast.error("Failed to fetch order");
            return;
        }

        var data = response.data as Category[];

        setCategories(data);
    };

    return (
        <div className="@container">
            <FormNav
                className={cn(
                    layout === LAYOUT_OPTIONS.BERYLLIUM && "2xl:top-[72px]",
                )}
                isAgent={isAgent}
            />
            <FormProvider {...methods}>
                <form
                    className={cn("mt-6", className)}
                    onSubmit={methods.handleSubmit(onSubmit)}
                >
                    <div className="mb-10 grid gap-7 divide-y divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11">
                        {Object.entries(
                            isAgent
                                ? MAP_AGENT_STEP_TO_COMPONENT
                                : MAP_STEP_TO_COMPONENT,
                        ).map(([key, Component]) => (
                            <Element
                                key={key}
                                name={
                                    isAgent
                                        ? FormAgentParts[
                                              key as keyof typeof FormAgentParts
                                          ]
                                        : FormParts[
                                              key as keyof typeof FormParts
                                          ]
                                }
                            >
                                <Component
                                    className="pt-7 @2xl:pt-9 @3xl:pt-11"
                                    categories={categories}
                                />
                            </Element>
                        ))}
                    </div>
                    <FormFooter
                        isLoading={isLoading}
                        handleAltBtn={() => navigate(-1)}
                        altBtnText="Cancel"
                        submitBtnText={
                            id
                                ? "Update Order"
                                : isAgent
                                  ? "Request Shipment"
                                  : "Create Order"
                        }
                    />
                </form>
            </FormProvider>
        </div>
    );
}
